import React, { useState, useEffect } from 'react';
import { CreditCard, DollarSign, CheckCircle, Clock, XCircle, FileText, Download, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const SoftwarePayment = () => {
  const [invoices, setInvoices] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [collegeInfo, setCollegeInfo] = useState(null);
  const [stats, setStats] = useState({
    total_invoices: 0,
    paid_count: 0,
    pending_count: 0,
    total_paid: 0,
    pending_amount: 0
  });
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    fetchData();
    loadCollegeInfo();
    
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const loadCollegeInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/tenants/my-college/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCollegeInfo({
          ...response.data,
          college_name: response.data.name || response.data.college_name,
        });
      }
    } catch (error) {
      console.error('Error fetching college info:', error);
    }
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch subscription status and invoices
      const [subscriptionRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/my-status`, { headers }),
        axios.get(`${API_URL}/api/subscriptions/my-invoices`, { headers })
      ]);

      setSubscription(subscriptionRes.data);
      
      const invoiceList = invoicesRes.data.invoices || invoicesRes.data || [];
      setInvoices(invoiceList);
      
      // Calculate stats
      const paidInvoices = invoiceList.filter(i => i.status === 'paid');
      const pendingInvoices = invoiceList.filter(i => i.status === 'pending' || i.status === 'overdue');
      
      setStats({
        total_invoices: invoiceList.length,
        paid_count: paidInvoices.length,
        pending_count: pendingInvoices.length,
        total_paid: paidInvoices.reduce((sum, i) => sum + (i.amount || 0), 0),
        pending_amount: pendingInvoices.reduce((sum, i) => sum + (i.amount || 0), 0)
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async (invoice) => {
    setPayingInvoice(invoice.id);
    
    try {
      const token = localStorage.getItem('token');
      
      // Create payment order for this invoice
      const orderResponse = await fetch(`${API_URL}/api/subscription-payments/pay-invoice-online`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          invoice_id: invoice.id
        })
      });
      
      if (!orderResponse.ok) {
        const error = await orderResponse.json();
        throw new Error(error.detail || 'Failed to create payment order');
      }
      
      const orderData = await orderResponse.json();
      
      // Open Razorpay
      const options = {
        key: orderData.key_id,
        amount: orderData.amount,
        currency: orderData.currency,
        name: collegeInfo?.college_name || 'Cloud Campus',
        description: `Invoice ${invoice.invoice_number}`,
        order_id: orderData.gateway_order_id,
        prefill: orderData.prefill,
        handler: async function(response) {
          // Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/api/subscription-payments/verify-invoice-payment`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                invoice_id: invoice.id,
                order_id: orderData.order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              })
            });
            
            if (verifyResponse.ok) {
              const result = await verifyResponse.json();
              toast.success(result.message || 'Payment successful! Subscription activated.');
              fetchData(); // Refresh data
            } else {
              const error = await verifyResponse.json();
              toast.error(error.detail || 'Payment verification failed');
            }
          } catch (err) {
            toast.error('Payment verification failed');
          }
        },
        modal: {
          ondismiss: function() {
            setPayingInvoice(null);
          }
        },
        theme: {
          color: '#3B82F6'
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      toast.error(error.message || 'Payment failed');
    } finally {
      setPayingInvoice(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-100 text-green-800 border-green-300',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      overdue: 'bg-red-100 text-red-800 border-red-300',
      cancelled: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return styles[status] || styles.pending;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'overdue': return <XCircle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  // Bug fix: removed shadowing formatDate – using imported one

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Payment History</h1>
        <p className="text-gray-600 mt-1">View your subscription invoices and make payments</p>
      </div>

      {/* Current Subscription Card */}
      {subscription && (
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 mb-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-lg font-medium opacity-90">Current Subscription</h2>
              <p className="text-3xl font-bold mt-2 capitalize">{subscription.plan_name || subscription.plan || 'No Plan'}</p>
              <p className="text-sm opacity-80 mt-1">
                {subscription.status === 'active' ? (
                  <>Valid until: {formatDate(subscription.subscription_end_date || subscription.end_date)}</>
                ) : (
                  <>Status: {subscription.status}</>
                )}
              </p>
            </div>
            <div className={`px-4 py-2 rounded-full text-sm font-medium ${
              subscription.status === 'active' ? 'bg-green-500' : 
              subscription.status === 'grace_period' ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
              {subscription.status === 'active' ? '● Active' : 
               subscription.status === 'grace_period' ? '● Grace Period' : '● ' + (subscription.status || 'Inactive')}
            </div>
          </div>
          {subscription.days_remaining !== undefined && subscription.days_remaining > 0 && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-sm opacity-80">
                {subscription.days_remaining} days remaining in current billing cycle
              </p>
            </div>
          )}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_invoices}</p>
            </div>
            <FileText className="h-10 w-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Paid</p>
              <p className="text-2xl font-bold text-green-600">
                ₹{stats.total_paid.toLocaleString('en-IN')}
              </p>
            </div>
            <DollarSign className="h-10 w-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Paid Invoices</p>
              <p className="text-2xl font-bold text-gray-900">{stats.paid_count}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Amount</p>
              <p className="text-2xl font-bold text-orange-600">
                ₹{stats.pending_amount.toLocaleString('en-IN')}
              </p>
            </div>
            <Clock className="h-10 w-10 text-orange-600" />
          </div>
        </div>
      </div>

      {/* Invoice History Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">Invoice History</h2>
          {stats.pending_count > 0 && (
            <span className="text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
              {stats.pending_count} pending payment(s)
            </span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">Invoice #</th>
                <th className="text-left p-4 font-semibold text-gray-700">Billing Period</th>
                <th className="text-left p-4 font-semibold text-gray-700">Amount</th>
                <th className="text-left p-4 font-semibold text-gray-700">Due Date</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700">Payment Date</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {invoices.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No invoices found</p>
                    <p className="text-sm mt-1">Your subscription invoices will appear here</p>
                  </td>
                </tr>
              ) : (
                invoices.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="p-4">
                      <span className="font-mono text-sm font-medium text-blue-600">
                        {invoice.invoice_number}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {formatDate(invoice.billing_period_start)} - {formatDate(invoice.billing_period_end)}
                    </td>
                    <td className="p-4">
                      <span className="font-semibold text-gray-900">
                        ₹{(invoice.amount || 0).toLocaleString('en-IN')}
                      </span>
                      <span className="text-xs text-gray-500 ml-1 capitalize">
                        ({invoice.billing_cycle || invoice.plan_type || 'monthly'})
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {formatDate(invoice.due_date)}
                    </td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        {invoice.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-gray-600">
                      {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                    </td>
                    <td className="p-4">
                      {invoice.status !== 'paid' && (
                        <button
                          onClick={() => handlePayNow(invoice)}
                          disabled={payingInvoice === invoice.id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {payingInvoice === invoice.id ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CreditCard className="h-4 w-4" />
                              Pay Now
                            </>
                          )}
                        </button>
                      )}
                      {invoice.status === 'paid' && (
                        <span className="text-sm text-green-600 font-medium">
                          ✓ Paid
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Help Text */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-900 mb-2">Payment Options</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• <strong>Online Payment:</strong> Click "Pay Now" to pay instantly via UPI, Card, or Net Banking</li>
          <li>• <strong>Offline Payment:</strong> Contact your administrator for bank transfer details</li>
        </ul>
      </div>
    </div>
  );
};

export default SoftwarePayment;
